import { useUsersAuthzCheckUserPermissionUserPermissions } from '@mi-front/api-v2';
import { UserRole } from '@mi-front/core';
import React from 'react';
import { useAuth } from '../../authentication/hooks/useAuth';

interface Props {
  readonly permission: string;
  readonly cant?: React.ReactElement | (() => React.ReactElement);
  readonly children: React.ReactElement;
}

/**
 * IfPermission component that checks permissions against the UsersAuthz API.
 *
 * For users with CLIENT_USER role, it checks the permission against the UsersAuthz API.
 * For all other roles (CLIENT_ADMIN, SUPERADMIN), permission is automatically granted.
 *
 * @example
 * ```tsx
 * <IfPermission permission="content:create">
 *   <button>Create Content</button>
 * </IfPermission>
 *
 * <IfPermission
 *   permission="content:delete"
 *   cant={() => <span>You don't have permission to delete content</span>}
 * >
 *   <button>Delete Content</button>
 * </IfPermission>
 * ```
 */
export const IfPermission = (p: Props): React.ReactElement => {
  const auth = useAuth();

  if (!auth || !auth.user) {
    throw new Error('Auth context or user not found');
  }

  const shouldCheckPermission = auth.user.role === UserRole.CLIENT_USER;

  const permissionCheck = useUsersAuthzCheckUserPermissionUserPermissions(p.permission, {
    query: {
      enabled: shouldCheckPermission,
    },
  });

  if (!shouldCheckPermission) {
    return p.children;
  }

  if (permissionCheck.isLoading) {
    return <></>;
  }

  if (permissionCheck.error) {
    console.error('Permission check failed:', permissionCheck.error);
    return p.cant ? typeof p.cant === 'function' ? p.cant() : p.cant : <></>;
  }

  if (permissionCheck.data?.hasPermission) {
    return p.children;
  }

  return p.cant ? typeof p.cant === 'function' ? p.cant() : p.cant : <></>;
};

IfPermission.defaultProps = {
  cant: () => <></>,
};
